// Cheap, nasty little avatars for the creepypastas. No model loading — just
// primitives with emissive materials so they read in the gloom.

import THREE from "../three.js";
import type { Group } from "three";

/** The pale face that presses against the dry-sauna window. */
export function buildFace(): { group: Group; press: (amt: number) => void } {
  const group = new THREE.Group(); // positioned at the window anchor; never moved
  const inner = new THREE.Group(); // the pressable bits (face + hands)
  group.add(inner);

  const skin = new THREE.MeshStandardMaterial({
    color: 0xcfc6bd,
    roughness: 1,
    emissive: 0x3a2418,
    emissiveIntensity: 0.7,
  });
  // A sickly cold glow from outside, so the face reads through the dark glass.
  const faceGlow = new THREE.PointLight(0x6f86a6, 0.6, 1.4, 1.5);
  faceGlow.position.set(0, 0.05, 0.25);
  inner.add(faceGlow);
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.16, 20, 20), skin);
  head.scale.set(1, 1.25, 0.8);
  inner.add(head);

  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x050505, emissive: 0x000000 });
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 12, 12), eyeMat);
    eye.position.set(sx * 0.06, 0.04, 0.12);
    inner.add(eye);
  }
  const mouth = new THREE.Mesh(
    new THREE.SphereGeometry(0.05, 12, 12),
    new THREE.MeshStandardMaterial({ color: 0x1a0606 }),
  );
  mouth.scale.set(1.3, 0.6, 0.4);
  mouth.position.set(0, -0.09, 0.12);
  inner.add(mouth);

  // Two splayed hands at the glass.
  for (const sx of [-1, 1]) {
    const hand = new THREE.Mesh(new THREE.SphereGeometry(0.07, 10, 10), skin);
    hand.scale.set(1.1, 1.4, 0.3);
    hand.position.set(sx * 0.22, -0.02, 0.1);
    inner.add(hand);
  }

  return {
    group,
    // press toward the glass as it escalates — moves the inner bits only, so the
    // root stays parked at the window anchor.
    press: (amt: number) => {
      inner.position.z = 0.12 * amt;
      head.scale.z = 0.8 + amt * 0.25;
    },
  };
}

/** The burnt figure that shares the steam bench. Must NOT be looked at. */
export function buildBurntMan(): { group: Group; head: THREE.Object3D; seethe: (t: number) => void } {
  const group = new THREE.Group();
  const char = new THREE.MeshStandardMaterial({
    color: 0x140d0a,
    roughness: 1,
    emissive: 0x822409,
    emissiveIntensity: 0.45,
  });

  const torso = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.7, 12), char);
  torso.position.y = 0.95;
  group.add(torso);

  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 16, 16), char);
  head.position.y = 1.4;
  group.add(head);

  // glowing cracks (a couple of emissive lines)
  for (let i = 0; i < 3; i++) {
    const crack = new THREE.Mesh(
      new THREE.BoxGeometry(0.02, 0.3, 0.02),
      new THREE.MeshStandardMaterial({ color: 0xff5a1e, emissive: 0xff5a1e, emissiveIntensity: 1.2 }),
    );
    crack.position.set(0.05 * (i - 1), 0.95 + i * 0.05, 0.18);
    group.add(crack);
  }

  // thighs (seated)
  for (const sx of [-1, 1]) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 0.4, 8), char);
    leg.rotation.x = Math.PI / 2;
    leg.position.set(sx * 0.08, 0.62, 0.18);
    group.add(leg);
  }

  return {
    group,
    head,
    seethe: (t: number) => {
      const f = 0.4 + 0.25 * Math.sin(t * 3);
      char.emissiveIntensity = f;
    },
  };
}
